import PropTypes from 'prop-types'
import { FieldArray } from 'formik'
import { TrashIcon, PlusIcon } from '@phosphor-icons/react'
import { Trans, useTranslation } from 'react-i18next'

import TextareaInput from '../formInputs/TextareaInput'
import TextField from '../../../../FormLegacy/FormFieldsV2/TextField'

import ToggleInput from './ToggleInput'
import FormInput from './FormInput'

// TODO: Move to a seperate file
const MAX_NUMBER_OF_TICKETS = 20

const TicketsSection = ({
  name,
  tickets = [],
  setFieldValue,
  advancedTicketingEnabled,
  addingAdditionalTicketsEnabled = true,
}) => {
  const { t } = useTranslation(['eventManagement'])
  const lastTicket = tickets[tickets.length - 1]
  const isAddTicketTypeButtonDisabled =
    !lastTicket?.name?.trim() ||
    !advancedTicketingEnabled ||
    tickets?.length >= MAX_NUMBER_OF_TICKETS

  const handleTicketInputChange = (index, name, value) => {
    setFieldValue(`tickets[${index}].${name}`, value)
  }

  return (
    <FieldArray name={name}>
      {(arrayHelpers) => {
        return (
          <div className="ticket-section">
            {tickets.map((ticket, index) => {
              const disabled = ticket.isUsed

              return (
                <div className="ticket-item" key={`ticket-${index}`}>
                  <div className="ticket-item__name-row">
                    <FormInput name="name" label={t('eventManagement:common.ticketTypeNameLabel')}>
                      <TextField
                        name="name"
                        onChange={(e) => {
                          handleTicketInputChange(index, 'name', e.target.value)
                        }}
                        value={ticket.name}
                        maxLength={30}
                        disabled={disabled}
                      />
                      <p className="event-form-input-helper-text">
                        {t('eventManagement:common.maxInputLength30')}
                      </p>
                    </FormInput>

                    <FormInput label={t('eventManagement:common.priceLabel')} name="price">
                      <TextField
                        type="number"
                        onlyIntegers
                        onChange={(e) => {
                          const value = e.target.value
                          const maxPrice = 1000000

                          if (value.includes('.')) {
                            return
                          }

                          if (parseFloat(value) > maxPrice) {
                            handleTicketInputChange(index, 'price', maxPrice)
                            return
                          }

                          handleTicketInputChange(index, 'price', value)
                        }}
                        value={ticket.price}
                        min={0}
                      />
                    </FormInput>

                    {index > 0 && (
                      <button
                        className="button button-white"
                        onClick={() => arrayHelpers.remove(index)}
                        disabled={disabled}
                      >
                        <TrashIcon size={20} />
                      </button>
                    )}
                  </div>
                  <div className="columns ticket-item__description-row">
                    <TextareaInput
                      columnSize={12}
                      name="description"
                      label={t('eventManagement:common.ticketTypeDescriptionLabel')}
                      placeholder={t('eventManagement:common.ticketTypeDescriptionPlaceholder')}
                      value={ticket.description}
                      setFieldValue={(name, value) => {
                        handleTicketInputChange(index, name, value)
                      }}
                      helperText={t('eventManagement:common.maxInputLength255')}
                      maxLength={255}
                    />
                  </div>
                  {advancedTicketingEnabled && (
                    <div className="ticket-item__inactive-row">
                      <p className="event-form-input__label">
                        {t('eventManagement:common.visibility')}
                      </p>
                      <ToggleInput
                        columnSize={12}
                        name={`inactive-${index}`}
                        value={ticket.inactive}
                        label={t('eventManagement:common.hideFromResidents')}
                        tooltip={
                          <>
                            <p>{t('eventManagement:common.ticketTooltipLine1')}</p>
                            <p>
                              <Trans
                                i18nKey="eventManagement:common.ticketTooltipLine2"
                                components={[<b key="0" />]}
                              />
                            </p>
                            <p>{t('eventManagement:common.ticketTooltipLine3')}</p>
                            <p>
                              <Trans
                                i18nKey="eventManagement:common.ticketTooltipLine4"
                                components={[<b key="0" />]}
                              />
                            </p>
                            <p>{t('eventManagement:common.ticketTooltipLine5')}</p>
                            <p>{t('eventManagement:common.ticketTooltipLine6')}</p>
                          </>
                        }
                        onToggle={(name, checked) => {
                          handleTicketInputChange(index, 'inactive', checked)
                        }}
                      />
                    </div>
                  )}
                </div>
              )
            })}
            {addingAdditionalTicketsEnabled && (
              <button
                className="button button-transparent add-ticket-type"
                onClick={() =>
                  arrayHelpers.push({ name: '', price: 0, description: '', inactive: false })
                }
                type="button"
                disabled={isAddTicketTypeButtonDisabled}
              >
                {t('eventManagement:common.addTicketType')}
                <PlusIcon size={20} />
              </button>
            )}
          </div>
        )
      }}
    </FieldArray>
  )
}

TicketsSection.propTypes = {
  name: PropTypes.string,
  tickets: PropTypes.array,
  setFieldValue: PropTypes.func.isRequired,
  advancedTicketingEnabled: PropTypes.bool.isRequired,
  addingAdditionalTicketsEnabled: PropTypes.bool,
}

export default TicketsSection
